import React, { useCallback, useContext, useEffect, useState } from 'react';
import i18next from 'i18next';
import { Context } from '../../../states/Store';
import EADialog from '../../common/EADialog';
import useStyles from "../../../style/js-style/containers/thirdparties/unmanagedThirdParty/CreateUnmanagedThirdPartyStyle";
import { useForm } from 'react-hook-form';
import DeclarationStep from './step/DeclarationStep';
import AttributesStep from '../AttributesStep';
import {
  getDefaultShownValuesIfExist, getDefaultValuesIfExist,
  category,
  UNMANAGED_TP_STEP, unmanagedThirdPartyMode,
} from './UnmanagedThirdPartyUtils';
import _ from 'lodash';
import { ERROR } from '../../../services/common/Constants';
import { getAccountAttributesByBondType } from '../../../services/edge/AttributesService';
import { createUnmanagedThirdparty, EditUnmanagedThirdparty } from '../../../services/edge/ThirdpartyService';
import { useParams } from 'react-router';
import ROUTES from '../../../config/Routes';
import { useHistory } from 'react-router-dom';
import EAColorIcon from '../../common/EAColorIcon';
import {
  formatActivityTextFromActivity,
  getCountries,
} from 'services/common/Utils';
import { getApiErrorMessageCode } from '../../../services/common/ApiUtils';

function UnmanagedThirdParty({ isOpen, onClose, mode = unmanagedThirdPartyMode.CREATE, thirdparty = {} }) {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const [progression, setProgression] = useState(20);
  const [currentStep, setCurrentStep] = useState(UNMANAGED_TP_STEP.initial);
  const [activity, setActivity] = useState(null);
  const [country, setCountry] = useState(null);
  const [isContinueClicked, setIsContinueClicked] = useState(false);
  const [hasAttributesToEdit, setHasAttributesToEdit] = useState(0);
  const [legalCategory, setLegalCategory] = useState([]);
  let { thirdpartyId: thirdpartyIdParam } = useParams();
  const thirdpartyId = thirdparty.thirdpartyId || thirdpartyIdParam;
  let history = useHistory();

  const getFormDefaultValues = () => {
    return mode === unmanagedThirdPartyMode.CREATE && thirdparty
      ? {
      activity: null,
      address: null,
      capital: null,
      capitalCurrency: "EUR",
      country: null,
      etabType: "headOffice",
      id_local: null,
      legalCategory: null,
      postalCode: null,
      socialName: null,
      category: null,
      town: null,
      attributes: []
    } : {
      activity: thirdparty.etabActivityCode || null,
      address: thirdparty.addressStreet || null,
      capital: thirdparty.capital || null,
      capitalCurrency: thirdparty.capitalCurrency || null,
      country: thirdparty.addressCountryCode || null,
      etabType: thirdparty.headquarter ? "headOffice" : "secondary",
      legalCategory: thirdparty.legalCategoryCode || null, 
      id_local: thirdparty.companyIdValue || null,
      postalCode: thirdparty.addressPostCode || null,
      socialName: thirdparty.name || null,
      category: thirdparty.category || null,
      town: thirdparty.addressCity || null,
      attributes: []
    }
  };

  const formMethods = useForm({
    mode: "onChange",
    delayError: 500,
    defaultValues: getFormDefaultValues(),
  });

  const {
    getValues,
    formState: { errors },
    clearErrors,
    trigger,
    setError,
    setValue
  } = formMethods;

  const watchActivity = formMethods.watch("activity");
  const watchCountry = formMethods.watch("country");
  const watchAttributes = formMethods.watch("attributes");

  useEffect(() => {
    setHasAttributesToEdit(watchAttributes.filter((entry) => entry.type !== 'calculated').length > 0);
  }, [watchAttributes]);

  const handleSubmitCreate = async () => {
    const formValues = formMethods.getValues();
    let thirdId = null;
    const unmanagedThirdParty = buildSubmitData(formValues);
    const attributesToSave = formMethods.getValues("attributes").map(attribute => ({
      label: attribute.label,
      type: attribute.type,
      values: attribute.values,
      id: attribute.attributeId,
    }));
   
    try {
      const response = await createUnmanagedThirdparty(state.account.id, unmanagedThirdParty, attributesToSave, dispatch);
      if (response?.content) {
        thirdId = response.content.thirdpartyId;
      }
      onClose();

    } catch (error) {
      if (error?.message?.messages?.error) {
        dispatch({
          type: "ALERT",
          alert: { type: ERROR, msg: getApiErrorMessageCode(error) }
        });
      }
    }

    // Redirect to thirdparty detail
    if (thirdId) {
      const route = ROUTES.THIRDPARTY_DETAIL.replace(
        ":thirdpartyId",
        thirdId
      );
      history.push(`/${state.account.id}/${route}`, {
        prevPath: history.location.pathname,
      });
    }

  }

  const handleSubmitEdit = async () => {
    try {
      const formValues = formMethods.getValues();
      const unmanagedThirdParty = buildSubmitData(formValues);

      await EditUnmanagedThirdparty(state.account.id, thirdpartyId,unmanagedThirdParty, dispatch);
      
      onClose();
    } catch (error) {
      if (error?.message?.messages?.error && error?.message?.messages?.error[0]?.code) {
        dispatch({
          type: "ALERT",
          alert: { type: ERROR, msg: i18next.t(`infos.INTERNAL_ERROR`) }
        });
      }
    }
  }

  const toAttributesStep = async () => {
    setIsContinueClicked(true);
    const isFormValid = await trigger();
    await checkSelectErrors();

    if (_.isEmpty(errors) && isFormValid) {
      setCurrentStep(UNMANAGED_TP_STEP.attributes);
    }
  }

  // Vérifie les erreurs pour les inputs de type select et obligatoire
  const checkSelectErrors = () => {
    const values = getValues();
    const valuesToCheck = [ "activity", "country" ]

    valuesToCheck.forEach(value => {
      if (!values[value]){
        setError(value, {
          type: 'manual',
          message: i18next.t("infos.required")
        });
      } else {
        clearErrors(value)
      }
    })
  }

  // Récupère la liste des attributs
  const fetchAttributes = useCallback(async () => {
    try {
      const results = await getAccountAttributesByBondType(state.account.id, 'thirdparty', null);
      const mappedAttributes = results.content.map((attribute) => {
        return {
          ...attribute,
          id: attribute.label,
          shownValues: getDefaultShownValuesIfExist(attribute),
          values: getDefaultValuesIfExist(attribute),
          attributeId: attribute.id
        };
      })
      setValue("attributes", mappedAttributes.sort((a, b) => a.label.localeCompare(b.label)))
    } catch (error) {

    }
  }, [state.account, setValue]);

  // Fetch la liste des catégories légales
  useEffect(() => {
    const legalCategories = Object.keys(state.legalActivityTraductions)
      .map((key) => ({value: key, label: state.legalActivityTraductions[key]} ) );
    
    setLegalCategory(legalCategories);
    
  }, [state.legalActivityTraductions]);

  // Contrôles les erreurs sur les selects à chaque modification de l'un d'eux
  useEffect(() => {
    isContinueClicked && checkSelectErrors()
  // eslint-disable-next-line
  }, [watchActivity, watchCountry]);

  // Récupère les pays et activités
  useEffect(() => {
    if (state.account) {
      async function fetchData() {
        try {
          const activities = state.activityTraductions
          .filter(a => a.origine === 'DNB')
          .sort((a, b) => formatActivityTextFromActivity(a).localeCompare(formatActivityTextFromActivity(b)))
          .map((activity) => {
            return {
              value: activity.code,
              label: formatActivityTextFromActivity(activity),
              level: activity.level,
              type: 'SIC', // on prend uniquement les activités DNB
              shortLabel: activity.description,
              code: activity.code
            };
          });
          setActivity(activities);
         
          let distinctCountries = [];
          let allCountries = getCountries(state.lang);
          for (const [key, value] of Object.entries(allCountries)) {
            distinctCountries.push({"value": key, "label": value});
          }
          const sortedMap = Array.from(distinctCountries).sort((a, b) => a.label.localeCompare(b.label));
          setCountry(sortedMap);

        } catch (e) {
          dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
        }
      }

      if (state.activityTraductions && state.lang){
        fetchData();
      }
    }
  }, [dispatch, state.account, state.activityTraductions, state.lang]);

  // Fetch des attributs uniquement si c'est en mode création
  useEffect(() => {
    if (state.account && mode === unmanagedThirdPartyMode.CREATE) {
      fetchAttributes();
    }
  }, [state.account, fetchAttributes,mode])

  const ProgressBar = () => {
    return (
      <div className={classes.progressBarWrapper}>
        <div
          className={classes.progressBarStep}
          style={{ width: progression + '%' }}
        />
      </div>
    );
  };

  // Gestion de la barre de progression
  useEffect(() => {
    if (mode === unmanagedThirdPartyMode.CREATE){
      if (currentStep === UNMANAGED_TP_STEP.initial){
        setProgression(20)
      }else{
        setProgression(50)
      }
    }else{
      setProgression(50)
    }

  }, [mode, currentStep]);

  const PopUpTitle = () => {
    return (
      <div className={classes.popUpTitle}>
        <EAColorIcon icon={'unmanagedThirdParty/unmanagedThirdParty'}  svgStyle={{ marginRight: "5px", position: "relative", top: "3px" }}/>
        { mode === unmanagedThirdPartyMode.CREATE
          ? i18next.t('unmanagedThirdParty.addThirdPartyTitle')
          : i18next.t('unmanagedThirdParty.editThirdPartyTitle')
        }
        { mode = unmanagedThirdPartyMode.EDIT && thirdparty.name &&
          <>
            &nbsp;-&nbsp;
            <span className={classes.popUpTitleTPName}>
              {thirdparty.name || ""}
            </span>
          </>
        }
      </div>
    );
  };

  return (
    <EADialog
      isOpen={isOpen}
      title={<PopUpTitle />}
      onCancel={
        currentStep === UNMANAGED_TP_STEP.initial
          ? () => onClose()
          : () => {
            setCurrentStep(UNMANAGED_TP_STEP.initial);
            clearErrors();
          }
      }
      onClose={() => onClose()}
      maxWidth="popupmd"
      fullWidth
      validateBtnLabel={
        currentStep === UNMANAGED_TP_STEP.initial
          ? i18next.t('continue')
          : i18next.t('validate')
      }
      onValidate={
        currentStep === UNMANAGED_TP_STEP.initial && mode === unmanagedThirdPartyMode.CREATE && hasAttributesToEdit
          ? () => toAttributesStep()
          : mode === unmanagedThirdPartyMode.CREATE
            ? () => handleSubmitCreate()
            : () => handleSubmitEdit()
      }
      customBtnStyle={{justifyContent: "flex-end !important", marginRight: "16px", marginTop: "5px"}}
      isValidateDisabled={!_.isEmpty(errors)}
      customDialogStyle={{padding: "0 !important"}}
    >

      {/* Progress bar */}
      <ProgressBar />

      <div style={{padding: "21px"}}>
        {/* Notice */}
        {currentStep === UNMANAGED_TP_STEP.initial &&
          <div className={classes.noticeWrapper}>
            <EAColorIcon icon={'unmanagedThirdParty/lightbulb'} svgStyle={{marginRight: "5px"}} />
            {i18next.t('unmanagedThirdParty.notice')}
          </div>
        }

        {/* Déclaration des informations */}
        {currentStep === UNMANAGED_TP_STEP.initial && (
          <DeclarationStep
            activity={activity}
            legalCategory={legalCategory}
            category={category}
            country={country}
            formMethods={formMethods}
          />
        )}

        {/* Gestion des attributs */}
        {currentStep === UNMANAGED_TP_STEP.attributes && (
          <AttributesStep
            formMethods={formMethods}
          />
        )}
      </div>
    </EADialog>
  );

  function buildSubmitData(formValues) {
    return {
      etabActivityCode: formValues.activity,
      addressStreet: formValues.address,
      capital: formValues.capital || 0,
      addressCountryCode: formValues.country,
      headquarter: formValues.etabType === "headOffice",
      companyIdValue: formValues.id_local,
      legalCategoryCode: formValues.legalCategory,
      addressPostCode: formValues.postalCode,
      name: formValues.socialName,
      category: formValues.category,
      addressCity: formValues.town,
      capitalCurrency: formValues.capitalCurrency,
    };
  }
}

export default UnmanagedThirdParty;
